import React, { useEffect } from "react";

import { faArrowRightArrowLeft, faDollarSign } from "@fortawesome/pro-regular-svg-icons";

import {
	type Money,
	addMoney,
	moneyEquals,
	moneyFromMajorFloat,
	moneyFromWire,
	percentOf,
	toMajorFloatAmount,
	toMinorUnits,
	zeroMoney,
} from "@performyard/money";
import {
	FormField,
	FormSubscribe,
	Icon,
	InputNumeric,
	InputWrapper,
	Spinner,
	Text,
	usePyForm,
} from "@performyard/py-components";

import { allotmentBudgetSchema } from "../types";

import { useSelectionSalarySummary } from "../../../../api/hooks/defs/compensation";

import { formatCurrency } from "../../utils";

const DEFAULT_ALLOTMENT_PERCENT = 0.03;

type AllotmentIncreaseInputProps = {
	/** Exact money. The numeric inputs below convert at their own edges. */
	totalSalary: Money;
	value: number | null | undefined;
	onChange: (value: number | null | undefined) => void;
	onBlur: () => void;
};

const AllotmentIncreaseInput = ({ totalSalary, value, onChange, onBlur }: AllotmentIncreaseInputProps) => {
	const setFixed = (next: number | null | undefined): void => {
		if (next === null || next === undefined) {
			onChange(undefined);
			return;
		}
		onChange(next);
	};

	// Ratio over exact minor units. A percent is not money, so the float result is fine;
	// deriving it from two floats was not.
	const totalSalaryMinorUnits = toMinorUnits(totalSalary);
	const percentValue =
		value != null && totalSalaryMinorUnits > 0
			? (toMinorUnits(moneyFromMajorFloat(value)) / totalSalaryMinorUnits) * 100
			: null;
	const fixedDisplayValue = value ?? null;

	const handlePercentInputChange = (val: number | null): void => {
		if (val === null || totalSalaryMinorUnits <= 0) {
			setFixed(undefined);
			return;
		}
		// `percentOf` on exact money. As a float multiply this drifted, and the result is
		// the cycle's persisted budget, not just a display figure.
		onChange(toMajorFloatAmount(percentOf(totalSalary, val)));
	};

	const handleFixedInputChange = (val: number | null): void => {
		if (val === null) {
			setFixed(undefined);
			return;
		}
		onChange(val);
	};

	return (
		<div className="grid grid-cols-[1fr_24px_1fr] items-end gap-4">
			<InputWrapper id="allotment-increase-percent" label="Percent Increase">
				<InputNumeric
					allowEmpty
					value={percentValue}
					onChange={handlePercentInputChange}
					onBlur={onBlur}
					placeholder="Enter percentage"
					precision={2}
				/>
			</InputWrapper>
			<div className="flex h-8 items-center justify-center">
				<Icon icon={faArrowRightArrowLeft} color="neutral-9" size={16} />
			</div>
			<InputWrapper id="allotment-increase-fixed" label="Fixed Increase">
				<InputNumeric
					allowEmpty
					value={fixedDisplayValue}
					onChange={handleFixedInputChange}
					onBlur={onBlur}
					placeholder="Enter dollar amount"
					leftIcon={faDollarSign}
					useThousandsSeparator
					precision={2}
					padDecimals
				/>
			</InputWrapper>
		</div>
	);
};

type CycleBudgetStepProps = {
	userSelectionId: string;
};

const CycleBudgetStep = ({ userSelectionId }: CycleBudgetStepProps) => {
	const { data, isLoading } = useSelectionSalarySummary(userSelectionId);
	const form = usePyForm<typeof allotmentBudgetSchema>();
	// The selection's eligible-compensation sum, exact. The numeric form inputs convert
	// at their own edges rather than forcing this to be a float for everyone.
	const totalSalary = moneyFromWire(data?.totalSalary) ?? zeroMoney();
	const totalSalaryMinorUnits = toMinorUnits(totalSalary);

	useEffect(() => {
		if (isLoading || totalSalaryMinorUnits <= 0) {
			return;
		}

		const { totalEligibleCompensation: previousTotal, allotmentIncreaseFixed: currentFixed } = form.getFormValues();
		const previousTotalMoney = moneyFromMajorFloat(previousTotal);

		// Compared as money, not as two floats. `previousTotal !== totalSalary` reported a
		// change for two sums that were the same amount, silently rewriting a budget the
		// user had already entered.
		if (
			toMinorUnits(previousTotalMoney) > 0 &&
			!moneyEquals(previousTotalMoney, totalSalary) &&
			typeof currentFixed === "number" &&
			currentFixed > 0
		) {
			// The implied percent is a ratio over exact minor units; applying it back to the
			// new total goes through `percentOf` so the re-derived budget does not drift
			// each time the employee selection changes.
			const impliedPercent =
				(toMinorUnits(moneyFromMajorFloat(currentFixed)) / toMinorUnits(previousTotalMoney)) * 100;
			const defaultPercent = DEFAULT_ALLOTMENT_PERCENT * 100;
			const nextPercent = Math.abs(impliedPercent - defaultPercent) < 0.01 ? defaultPercent : impliedPercent;
			form.setFieldValue("allotmentIncreaseFixed", toMajorFloatAmount(percentOf(totalSalary, nextPercent)));
		}

		if (previousTotal === 0 && (currentFixed === undefined || currentFixed === null)) {
			form.setFieldValue(
				"allotmentIncreaseFixed",
				toMajorFloatAmount(percentOf(totalSalary, DEFAULT_ALLOTMENT_PERCENT * 100))
			);
		}

		form.setFieldValue("totalEligibleCompensation", toMajorFloatAmount(totalSalary));
		// The exact value travels alongside the rounded one; the request sends this.
		form.setFieldValue("wireTotalEligibleCompensation", data?.totalSalary);
	}, [form, isLoading, totalSalary, totalSalaryMinorUnits, data?.totalSalary]);

	if (isLoading) {
		return (
			<div className="flex items-center justify-center py-12">
				<Spinner />
			</div>
		);
	}
	return (
		<div className="mx-auto flex max-w-[512px] flex-col gap-8">
			<div className="text-center">
				<Text size="sub-heading" weight="semi-bold">
					Create a Compensation Cycle
				</Text>
				<Text size="paragraph-2" weight="regular">
					Cycle Budget
				</Text>
			</div>

			<div className="flex flex-col gap-4 bg-neutral-1 p-6">
				<div>
					<Text size="caption">Total Eligible Compensation</Text>
					<Text size="heading-3" weight="semi-bold">
						{formatCurrency(totalSalary)}
					</Text>
				</div>

				<div className="flex flex-col gap-3">
					<Text size="caption" weight="regular">
						Define Budget
					</Text>
					<FormField<number | null | undefined>
						id="allotmentIncreaseFixed"
						input={({ value, onChange, onBlur }) => (
							<AllotmentIncreaseInput
								totalSalary={totalSalary}
								value={value as number | null | undefined}
								onChange={(v) => onChange(v as number | null | undefined)}
								onBlur={onBlur}
							/>
						)}
					/>
				</div>
				<div>
					<Text size="paragraph-3" weight="semi-bold">
						New Compensation Total
					</Text>
					<FormSubscribe
						id="allotmentIncreaseFixed-subscription"
						selector={(state: { values: { allotmentIncreaseFixed: number | null | undefined } }) =>
							state.values.allotmentIncreaseFixed
						}
					>
						{(allotmentIncreaseFixed) => (
							<div className="mt-2 rounded-1 bg-neutral-2 p-2">
								<Text size="paragraph-2" weight="semi-bold">
									{/* Summed exactly, with no `Math.floor`. Flooring discarded up to 99 cents,
									    so this preview disagreed with both the figure on the review step and
									    the amount actually persisted. */}
									{formatCurrency(
										addMoney(totalSalary, moneyFromMajorFloat(allotmentIncreaseFixed ?? 0))
									)}
								</Text>
							</div>
						)}
					</FormSubscribe>
				</div>
			</div>
		</div>
	);
};

export default CycleBudgetStep;
